import axios from 'axios';

class ApiService {
	constructor() {
		this.api = axios.create({
			baseURL: process.env.REACT_APP_API_URL || '/api',
			timeout: 30000,
			headers: {
				'Content-Type': 'application/json',
			},
		});
		console.log(this.api.baseURL)

		// Request interceptor
		this.api.interceptors.request.use(
			(config) => {
				const token = localStorage.getItem('kaino_token');
				const currentApiKey = localStorage.getItem(
					'kaino_current_api_key',
				);

				// Check if this is an external API endpoint that needs API key
				// Most dashboard endpoints should use JWT tokens instead
				const isExternalApiEndpoint =
					config.url.includes('/credit/') ||
					config.url.includes('/debt/') ||
					config.url.includes('/direct-debit/');

				if (isExternalApiEndpoint && currentApiKey) {
					config.headers['X-API-Key'] = currentApiKey;
				} else if (token) {
					config.headers.Authorization = `Bearer ${token}`;
				}

				return config;
			},
			(error) => {
				return Promise.reject(error);
			},
		);

		// Response interceptor
		this.api.interceptors.response.use(
			(response) => response,
			(error) => {
				if (error.response?.status === 401) {
					// Check if we're already on the login page or in the middle of login
					const isOnLoginPage = window.location.pathname === '/login';
					const isLoginEndpoint =
						error.config?.url?.includes('/institution/login');

					// Don't redirect if we're already on login page or this is a login request
					if (isOnLoginPage || isLoginEndpoint) {
						return Promise.reject(error);
					}

					localStorage.removeItem('kaino_token');
					localStorage.removeItem('kaino_user');
					localStorage.removeItem('kaino_current_api_key');
					localStorage.removeItem('kaino_institution_id');
					localStorage.removeItem('kaino_current_environment');
					// Remove legacy keys as well
					localStorage.removeItem('kaino_api_key');

					// Use a slight delay to ensure any state updates are processed
					setTimeout(() => {
						window.location.href = '/login';
					}, 100);
				}
				return Promise.reject(error);
			},
		);
	}

	setAuthToken(token) {
		console.log(token);
		if (token) {
			this.api.defaults.headers.Authorization = `Bearer ${token}`;
		} else {
			delete this.api.defaults.headers.Authorization;
		}
	}

	// Generic methods
	async get(url, config = {}) {
		return this.api.get(url, config);
	}

	async post(url, data = {}, config = {}) {
		return this.api.post(url, data, config);
	}

	async put(url, data = {}, config = {}) {
		return this.api.put(url, data, config);
	}

	async delete(url, config = {}) {
		return this.api.delete(url, config);
	}

	// Credit endpoints - simplified to only take BVN
	async performCreditCheck(data) {
		return this.post('/credit/check', data);
	}

	// Debt endpoints - simplified to single endpoint
	async getDebts() {
		return this.get('/debt');
	}

	async getDebt(id) {
		return this.get(`/debt/${id}`);
	}

	async getUserDebts(userId) {
		return this.get(`/debt/user/${userId}`);
	}

	async createDebt(data) {
		return this.post('/credit/consent', data);
	}

	async updateDebt(id, data) {
		return this.put(`/debt/${id}`, data);
	}

	async markDebtAsDefault(debtId) {
		return this.post(`/debt/${debtId}/default`);
	}

	// Notification endpoints
	async getNotificationPreferences() {
		return this.get('/notifications/preferences');
	}

	async updateNotificationPreferences(data) {
		return this.put('/notifications/preferences', data);
	}

	async getNotificationLogs() {
		return this.get('/notifications/logs');
	}

	async sendTestNotification() {
		return this.post('/notifications/test');
	}

	async getNotificationStatistics() {
		return this.get('/notifications/statistics');
	}

	// Institution endpoints - updated to use correct endpoint structure
	async getInstitution(institutionId) {
		// For dashboard requests, use JWT with internal request header
		return this.get(`/institution/${institutionId}`, {
			headers: {
				'x-internal-request': 'true'
			}
		});
	}

	async updateInstitution(institutionId, data) {
		return this.put(`/institution/${institutionId}`, data);
	}

	async resetApiKey(institutionId) {
		return this.post(`/institution/${institutionId}/reset-api-key`);
	}

	// Institution Keys endpoints - new environment-specific API keys management
	async getInstitutionKeys() {
		return this.get('/institution-keys');
	}

	async resetTestKeys() {
		return this.post('/institution-keys/reset-test');
	}

	async activateLiveKeys() {
		return this.post('/institution-keys/activate-live');
	}

	async resetLiveKeys(confirmationCode) {
		return this.post('/institution-keys/reset-live', { confirmationCode });
	}

	async getKeysUsage() {
		return this.get('/institution-keys/usage');
	}

	async validateApiKey() {
		return this.get('/institution-keys/validate');
	}

	// Direct Debit endpoints
	async getDirectDebits() {
		return this.get('/direct-debit');
	}

	async getDirectDebit(id) {
		return this.get(`/direct-debit/${id}`);
	}

	async createDirectDebit(data) {
		return this.post('/direct-debit', data);
	}

	async updateDirectDebit(id, data) {
		return this.put(`/direct-debit/${id}`, data);
	}

	async cancelDirectDebit(id) {
		return this.post(`/direct-debit/${id}/cancel`);
	}

	async processDirectDebit(id) {
		return this.post(`/direct-debit/${id}/process`);
	}

	async getDirectDebitHistory(id) {
		return this.get(`/direct-debit/${id}/history`);
	}

	// Admin endpoints (if user has admin access)
	async getAllInstitutions() {
		return this.get('/admin/institutions');
	}

	async getSystemStats() {
		return this.get('/admin/stats');
	}
}

export const apiService = new ApiService();
